import { useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { Camera, Church, Pencil, ArrowLeft } from "lucide-react";
import PostCard, { Avatar, SupporterBadge } from "@/components/social/PostCard";

function BioEditor({ bio, onSave, onCancel }) {
  const [val, setVal] = useState(bio);
  return (
    <div className="mt-4 w-full max-w-lg" data-testid="profile-bio-editor">
      <textarea value={val} onChange={(e) => setVal(e.target.value.slice(0, 300))} rows={3} placeholder="A sentence or two about you…" className="w-full px-3 py-2 text-sm" data-testid="profile-bio-input" />
      <div className="flex items-center justify-between mt-2">
        <span className="text-xs" style={{ color: "var(--muted)" }} data-testid="profile-bio-count">{val.length}/300</span>
        <div className="flex gap-2">
          <button onClick={onCancel} className="btn-ghost text-xs" style={{ padding: "0.4rem 0.9rem" }}>Cancel</button>
          <button onClick={() => onSave(val)} className="btn-primary text-xs" style={{ padding: "0.4rem 0.9rem" }} data-testid="profile-bio-save">Save</button>
        </div>
      </div>
    </div>
  );
}

function ProfileHeader({ p, setP }) {
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const savePhoto = async (f) => {
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { const r = await api.post("/social/profile/photo", fd, { headers: { "Content-Type": "multipart/form-data" } }); setP(r.data); toast.success("Photo updated."); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed."); }
    finally { setBusy(false); }
  };
  const saveBio = async (bio) => {
    try { const r = await api.put("/social/profile", { bio }); setP(r.data); setEditing(false); toast.success("Profile saved."); }
    catch (err) { toast.error(err?.response?.data?.detail || "Couldn't save."); }
  };
  return (
    <section className="rounded-2xl p-6 sm:p-8 flex flex-col items-center text-center" style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }} data-testid="profile-header">
      <div className="relative">
        <div className="rounded-full p-1" style={{ background: p.supporter ? "linear-gradient(135deg, var(--gold), #A78BFA)" : "var(--border-strong)" }}>
          <Avatar name={p.name} photo={p.photo_url} size={112} testid="profile-photo" />
        </div>
        {p.is_me && (
          <button onClick={() => fileRef.current?.click()} disabled={busy} className="absolute bottom-1 right-1 w-9 h-9 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-105" style={{ background: "var(--gold)", color: "var(--bg-base)" }} aria-label="Change photo" data-testid="profile-photo-btn">
            <Camera size={16} />
          </button>
        )}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) savePhoto(f); }} data-testid="profile-photo-input" />
      </div>
      <h1 className="font-serif-display text-3xl sm:text-4xl tracking-[-0.03em] mt-5" style={{ color: "var(--ink)" }} data-testid="profile-name">{p.name}</h1>
      <div className="mt-2"><SupporterBadge show={p.supporter} testid="profile-supporter-badge" /></div>
      {p.orgs.length > 0 && (
        <div className="mt-3 flex flex-wrap justify-center gap-1.5" data-testid="profile-orgs">
          {p.orgs.map((o) => <span key={o} className="inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full" style={{ background: "var(--surface-hover)", color: "var(--ink-2)" }}><Church size={11} /> {o}</span>)}
        </div>
      )}
      {editing ? <BioEditor bio={p.bio} onSave={saveBio} onCancel={() => setEditing(false)} /> : (
        <>
          {p.bio ? <p className="mt-4 text-sm max-w-lg leading-relaxed" style={{ color: "var(--ink-2)" }} data-testid="profile-bio">{p.bio}</p>
            : p.is_me && <p className="mt-4 text-sm italic" style={{ color: "var(--muted)" }}>Add a short bio so your church family can get to know you.</p>}
          {p.is_me && <button onClick={() => setEditing(true)} className="btn-ghost text-xs mt-4" style={{ padding: "0.4rem 0.9rem" }} data-testid="profile-edit-bio"><Pencil size={12} /> {p.bio ? "Edit bio" : "Add bio"}</button>}
        </>
      )}
    </section>
  );
}

export default function SocialProfile() {
  const { userId } = useParams();
  const { user, loading } = useAuth();
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!user) return;
    setP(null); setErr("");
    api.get(`/social/profile/${userId}`).then((r) => setP(r.data)).catch(() => setErr("Profile not found."));
  }, [userId, user]);

  if (loading) return null;
  if (!user) return <Navigate to={`/login?next=/social/u/${userId}`} replace />;

  const update = (np) => setP((x) => ({ ...x, posts: x.posts.map((y) => (y.id === np.id ? np : y)) }));
  const remove = (id) => setP((x) => ({ ...x, posts: x.posts.filter((y) => y.id !== id) }));

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10" data-testid="social-profile-page">
      <Link to="/social" className="inline-flex items-center gap-1 text-xs mb-5 hover:underline" style={{ color: "var(--muted)" }} data-testid="profile-back"><ArrowLeft size={12} /> Back to feed</Link>
      {err && <p style={{ color: "#F87171" }}>{err}</p>}
      {!p && !err && <p style={{ color: "var(--muted)" }}>Loading…</p>}
      {p && (
        <div className="space-y-4">
          <ProfileHeader p={p} setP={setP} />
          <h2 className="overline pt-4" style={{ color: "var(--gold)" }}>Recent posts</h2>
          {p.posts.length === 0 && <p className="text-sm text-center py-8" style={{ color: "var(--muted)" }} data-testid="profile-no-posts">No posts to show yet.</p>}
          {p.posts.map((post) => <PostCard key={post.id} post={post} canContribute onChange={update} onRemove={remove} />)}
        </div>
      )}
    </div>
  );
}
